import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { documentService } from '@services/document.service';
import { resolveApiErrorMessage } from '@utils/apiErrorMessages';

export type UploadStatus = 'pending' | 'uploading' | 'processing' | 'completed' | 'error' | 'duplicate';

export interface UploadMetadata {
  category: string;
  tags: string[];
  description: string;
  permissions?: unknown;
  existingVersion?: number;
  existingTitle?: string;
  existingDescription?: string;
  existingFileSize?: number;
  existingUploadedAt?: string;
}

export interface UploadFile {
  id: string;
  file: File;
  status: UploadStatus;
  progress: number;
  error?: string;
  statusMessage?: string;
  isDuplicate?: boolean;
  duplicateDocumentId?: string;
  willCreateNewVersion?: boolean;
  documentId?: string;
  metadata?: UploadMetadata;
}

export type DuplicateAction = 'replace' | 'keep' | 'cancel';

interface Options {
  /** Kept outside so the copy stays with the translations. */
  describeBackgroundProcessing: () => string;
}

const POLL_INTERVAL_MS = 1000;
const MAX_POLLS = 30;

export function useDocumentUpload({ describeBackgroundProcessing }: Options) {
  const { t } = useTranslation();
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [duplicate, setDuplicate] = useState<UploadFile | null>(null);
  const nextId = useRef(0);

  const patch = useCallback((id: string, changes: Partial<UploadFile>) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...changes } : f)));
  }, []);

  const upload = useCallback(
    async (target: UploadFile, asNewVersion?: boolean) => {
      patch(target.id, { status: 'uploading' });

      try {
        const response = await documentService.uploadDocument(
          target.file,
          {
            title: target.file.name,
            description: target.metadata?.description || '',
            category: target.metadata?.category || 'General',
            tags: target.metadata?.tags || [],
          },
          // Held at 90 until processing confirms; 100 would claim it is done.
          (progress: number) => patch(target.id, { progress: Math.min(progress, 90) }),
          { asNewVersion: asNewVersion || target.willCreateNewVersion },
          t,
        );

        const inBackground = () =>
          patch(target.id, {
            status: 'processing',
            progress: 95,
            documentId: response.id,
            statusMessage: describeBackgroundProcessing(),
          });

        inBackground();

        for (let poll = 0; poll < MAX_POLLS; poll += 1) {
          await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

          let status;
          try {
            status = await documentService.getUploadStatus(response.id);
          } catch {
            // Processing continues server-side; stop polling, do not fail the file.
            inBackground();
            return;
          }

          if (status.status === 'completed') {
            patch(target.id, {
              status: 'completed',
              progress: 100,
              documentId: response.id,
              statusMessage: undefined,
            });
            return;
          }
          if (status.status === 'failed') throw new Error(status.message || 'Processing failed');
          inBackground();
        }
      } catch (error: any) {
        if (error?.isDuplicate) {
          const flagged: UploadFile = {
            ...target,
            status: 'duplicate',
            progress: 100,
            // Already localized at the throw site (documents.duplicateUpload).
            error: error.message,
            isDuplicate: true,
            duplicateDocumentId: error.existingDocument?.id,
            metadata: {
              category: target.metadata?.category || 'general',
              tags: target.metadata?.tags || [],
              description: target.metadata?.description || '',
              permissions: target.metadata?.permissions || {},
              existingVersion: error.existingDocument?.currentVersion || 1,
              existingTitle: error.existingDocument?.title,
              existingDescription: error.existingDocument?.description,
              existingFileSize: error.existingDocument?.fileSize,
              existingUploadedAt: error.existingDocument?.uploadedAt,
            },
          };
          setFiles((prev) => prev.map((f) => (f.id === target.id ? flagged : f)));
          setDuplicate(flagged);
          return;
        }

        patch(target.id, {
          status: 'error',
          progress: 100,
          error: resolveApiErrorMessage(t, error, { fallbackKey: 'upload.uploadFailed' }),
        });
      }
    },
    [describeBackgroundProcessing, patch, t],
  );

  const addFiles = useCallback(async (selected: File[]) => {
    const queued: UploadFile[] = selected.map((file) => {
      nextId.current += 1;
      return { id: `upload-${nextId.current}`, file, status: 'pending', progress: 0 };
    });
    setFiles((prev) => [...prev, ...queued]);

    for (const entry of queued) {
      const result = await documentService.checkDuplicate(entry.file.name);
      if (!result.isDuplicate || !result.existingDocument) continue;

      const flagged: UploadFile = {
        ...entry,
        status: 'duplicate',
        isDuplicate: true,
        duplicateDocumentId: result.existingDocument.id,
        metadata: {
          category: '',
          tags: [],
          description: '',
          existingVersion: result.existingDocument.version,
          existingTitle: result.existingDocument.title,
          existingDescription: result.existingDocument.description,
          existingFileSize: result.existingDocument.fileSize,
          existingUploadedAt: result.existingDocument.uploadedAt,
        },
      };
      setFiles((prev) => prev.map((f) => (f.id === entry.id ? flagged : f)));
      setDuplicate((current) => current ?? flagged);
    }
  }, []);

  const removeFile = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
    setDuplicate((current) => (current?.id === id ? null : current));
  }, []);

  const resolveDuplicate = useCallback(
    async (action: DuplicateAction) => {
      const target = duplicate;
      setDuplicate(null);
      if (!target) return;

      if (action === 'cancel') {
        removeFile(target.id);
      } else if (action === 'keep') {
        patch(target.id, {
          status: 'pending',
          progress: 0,
          isDuplicate: false,
          duplicateDocumentId: undefined,
        });
      } else {
        const asVersion: UploadFile = {
          ...target,
          status: 'pending',
          progress: 0,
          isDuplicate: false,
          duplicateDocumentId: undefined,
          willCreateNewVersion: true,
        };
        setFiles((prev) => prev.map((f) => (f.id === target.id ? asVersion : f)));
        await upload(asVersion, true);
      }

      // Surface the next unresolved duplicate, if the batch had more than one.
      setFiles((prev) => {
        const next = prev.find((f) => f.status === 'duplicate' && f.id !== target.id);
        if (next) setDuplicate(next);
        return prev;
      });
    },
    [duplicate, patch, removeFile, upload],
  );

  const startUpload = useCallback(
    async (metadata: UploadMetadata) => {
      setIsUploading(true);
      const queued = files.filter((f) => f.status === 'pending').map((f) => ({ ...f, metadata }));
      setFiles((prev) => prev.map((f) => (f.status === 'pending' ? { ...f, metadata } : f)));

      for (const entry of queued) {
        await upload(entry);
      }
      setIsUploading(false);
    },
    [files, upload],
  );

  const retryFile = useCallback(
    (id: string) => patch(id, { status: 'pending', progress: 0, error: undefined }),
    [patch],
  );

  const canAdvance = useMemo(
    () => files.length > 0 && !files.some((f) => f.status === 'duplicate'),
    [files],
  );

  const completedDocumentId = useMemo(
    () => files.find((f) => f.status === 'completed' && f.documentId)?.documentId,
    [files],
  );

  return {
    files,
    isUploading,
    duplicate,
    canAdvance,
    completedDocumentId,
    addFiles,
    removeFile,
    resolveDuplicate,
    startUpload,
    retryFile,
  };
}
